import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  adminApi,
  type NotificationRolloutEntry,
  type NotificationRow,
  type NotificationDriftItem,
} from '../../api/adminApi';
import './Notifications.css';

/**
 * Notification rollout.
 *
 * Rows come from the code registry, never from the database — that is the whole
 * point. What the database contributes is per-row: how far each is rolled out,
 * and whether a rule actually backs it.
 *
 * Design: docs/FeatureDesigns/notification-registry-and-rollout.md
 */

const ALL_RINGS = ['devtesting', 'beta', 'prod', 'guest'] as const;
type Ring = typeof ALL_RINGS[number];

interface RowState {
  enabled: boolean;
  rings: Ring[];
}

/** Absent rollout means off — the same reading the backend uses, and there is no
 *  code default to reconcile against. */
function initialState(row: NotificationRow): RowState {
  return {
    enabled: !!row.rollout?.enabled,
    rings: (row.rollout?.enabledForRings || []) as Ring[],
  };
}

function DriftBlock({ title, hint, items }: { title: string; hint: string; items: NotificationDriftItem[] }) {
  if (items.length === 0) return null;
  return (
    <div className="nr-drift-block">
      <span className="nr-drift-label">{title} ({items.length})</span>
      <p className="nr-drift-hint">{hint}</p>
      <ul className="nr-drift-list">
        {items.map((d) => <li key={d.id}>{d.detail}</li>)}
      </ul>
    </div>
  );
}

export function Notifications() {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['notificationRollout'],
    queryFn: adminApi.getNotificationRollout,
  });

  useEffect(() => {
    if (!data?.notifications) return;
    const next: Record<string, RowState> = {};
    for (const row of data.notifications) next[row.id] = initialState(row);
    setRows(next);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => {
      // Every row is written, not only the edited ones, so the document always
      // states the whole picture rather than the subset someone happened to
      // touch. Reading it should never require knowing what is missing.
      const payload: Record<string, NotificationRolloutEntry> = {};
      for (const [id, state] of Object.entries(rows)) {
        payload[id] = {
          enabled: state.enabled,
          ...(state.rings.length > 0 ? { enabledForRings: state.rings } : {}),
        };
      }
      return adminApi.updateNotificationRollout(payload);
    },
    onSuccess: () => {
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['notificationRollout'] });
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 4000);
    },
  });

  const update = (id: string, patch: Partial<RowState>) =>
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const dirty = useMemo(() => {
    if (!data?.notifications) return false;
    return data.notifications.some((row) => {
      const now = rows[row.id];
      if (!now) return false;
      const was = initialState(row);
      return now.enabled !== was.enabled
        || now.rings.length !== was.rings.length
        || now.rings.some((r) => !was.rings.includes(r));
    });
  }, [rows, data]);

  const drift = data?.drift;

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Notifications</h2>
        <p className="section-description">
          Every notification this product can send, declared in code. This is the only switch —
          a rule existing wires a notification up, this decides whether anyone receives it, and
          anything not listed on below is off. Rolling one out is permission to send: the user's own
          category preferences still decide whether push arrives. Turning a ring off stops future
          sends; it does not recall what is already queued.
        </p>
      </div>

      {isLoading ? (
        <div className="loading">Loading notifications...</div>
      ) : (
        <>
          <div className="nr-list">
            {(data?.notifications || []).map((row) => {
              const state = rows[row.id];
              if (!state) return null;
              return (
                <div key={row.id} className={`nr-card ${!row.ruleExists ? 'nr-card-inert' : ''}`}>
                  <div className="nr-card-top">
                    <div className="nr-toggle-area">
                      <label className="nr-switch">
                        <input
                          type="checkbox"
                          checked={state.enabled}
                          onChange={(e) => update(row.id, { enabled: e.target.checked })}
                        />
                        <span className="nr-slider" />
                      </label>
                      <span className={`nr-status ${state.enabled ? 'nr-on' : 'nr-off'}`}>
                        {!state.enabled ? 'OFF' : state.rings.length === 0 ? 'ALL' : state.rings.join(', ')}
                      </span>
                    </div>

                    <div className="nr-badges">
                      {!row.ruleExists && (
                        <span className="nr-badge nr-badge-warn" title="No rule with this id exists in this environment, so this notification can never fire however it is rolled out.">
                          ⚠ no rule
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="nr-meta">
                    <span className="nr-label">{row.label}</span>
                    <code className="nr-event">{row.eventType}</code>
                    {row.category
                      ? <span className="nr-category">{row.category}</span>
                      : <span className="nr-category nr-category-none" title="Admin/ops notification — no user-facing toggle by design.">no user toggle</span>}
                  </div>
                  <p className="nr-description">{row.description}</p>

                  {state.enabled && (
                    <div className="nr-rings">
                      <span className="nr-rings-title">Rings</span>
                      <span className="nr-rings-hint">{state.rings.length === 0 ? '(everyone)' : ''}</span>
                      <div className="nr-ring-checks">
                        {ALL_RINGS.map((ring) => (
                          <label key={ring} className="nr-ring-label">
                            <input
                              type="checkbox"
                              checked={state.rings.includes(ring)}
                              onChange={(e) => update(row.id, {
                                rings: e.target.checked
                                  ? [...state.rings, ring]
                                  : state.rings.filter((r) => r !== ring),
                              })}
                            />
                            {ring}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {drift && !drift.clean && (
            <div className="nr-drift">
              <h3 className="nr-drift-title">Registry ↔ environment drift</h3>
              <DriftBlock
                title="Declared, but no rule here"
                hint="Nothing implements these in this environment, so they can never fire however they are rolled out. Seed the rule, or remove the declaration."
                items={drift.declaredWithoutRule}
              />
              <DriftBlock
                title="Rule here, not declared"
                hint="A rule exists that the registry does not know about — rollout cannot gate it and nothing documents what it sends. Usually a hand-edit in Cosmos. Declare it in notifications/registry.ts or delete the rule."
                items={drift.ruleWithoutDeclaration}
              />
              <DriftBlock
                title="Rollout for an unknown notification"
                hint="A stored rollout entry for an id the code no longer declares — a switch wired to nothing. Saving this page drops it."
                items={drift.rolloutForUnknownNotification}
              />
              <DriftBlock
                title="Declared for an event nothing emits"
                hint="The registry lists an event type no code sends, so the rule behind it can never match."
                items={drift.declaredForUnknownEventType}
              />
            </div>
          )}

          <div className="feature-actions">
            <button
              className="feature-save-btn"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !dirty}
              title={dirty ? undefined : 'No changes to save'}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save Rollout'}
            </button>
          </div>
          {saveStatus === 'saved' && <span className="status-saved feature-status">Rollout saved!</span>}
          {saveStatus === 'error' && <span className="status-error feature-status">{saveError || 'Failed to save'}</span>}

          {data?.updatedAt && (
            <p className="nr-updated">
              Last rolled out {format(new Date(data.updatedAt), 'PPpp')}
              {data.updatedBy ? ` by ${data.updatedBy}` : ''}
            </p>
          )}
        </>
      )}
    </div>
  );
}
